import React, { useState } from "react";
import { Box, Button, ButtonBase, Chip, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { Add, Close, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import useEditorMapa, { useMapaActual, useTarifas } from "../../stores/useEditorMapa";
import { MODO, quitarTarifa, sectorDeTarifa, capacidadSector, coloresDeTarifas, fijaPorVentas } from "../../domain/mapa/mapa";
import { asignarTarifa, motivoNoAsignar, gruposDe } from "../../domain/mapa/asignar";
import { partesDe } from "../../domain/mapa/partes";
import { elementosDe, resumen } from "../../domain/mapa/seleccion";
import { COLOR } from "./estilos";
import { BotonConfigurarMesa } from "./ConfigurarMesa";
import { DialogoTarifaNueva } from "./TarifaNueva";
import { quitarTarifaNueva } from "../../domain/mapa/tarifasNuevas";

export const ANCHO_PANEL_TARIFAS = 320;

export const formatoPrecio = (precio) =>
  Number(precio) > 0
    ? new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(precio)
    : "Gratis";

const OPCIONES_MODO = [
  { valor: MODO.POR_ZONA, titulo: "Por zona", detalle: "Cada tarifa abre sólo su ubicación" },
  { valor: MODO.MAPA_EVENTO, titulo: "Mapa del evento", detalle: "Se elige desde el plano completo" },
];

// SelectorModo: cómo elige el comprador su lugar. Afecta a las tarifas con
// lugares; las generales se venden por cantidad en los dos casos.
const SelectorModo = () => {
  const mapa = useMapaActual();
  const cambiar = useEditorMapa((s) => s.cambiar);
  return (
    <Box sx={{ px: 2.5, pt: 2, pb: 2, borderBottom: `1px solid ${COLOR.borde}` }}>
      <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: COLOR.textoSuave, mb: 1, textTransform: "uppercase" }}>
        Cómo elige el lugar el comprador
      </Typography>
      <ToggleButtonGroup
        exclusive
        orientation="vertical"
        size="small"
        fullWidth
        value={mapa.modo}
        onChange={(_, modo) => modo && cambiar((m) => ({ ...m, modo }))}
      >
        {OPCIONES_MODO.map((o) => (
          <ToggleButton key={o.valor} value={o.valor} sx={{ textTransform: "none", justifyContent: "flex-start", textAlign: "left" }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{o.titulo}</Typography>
              <Typography sx={{ fontSize: 12, color: COLOR.textoSuave }}>{o.detalle}</Typography>
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

const ItemTarifa = ({ tarifa, color, onEditar }) => {
  const mapa = useMapaActual();
  const cambiar = useEditorMapa((s) => s.cambiar);
  const seleccionar = useEditorMapa((s) => s.seleccionar);
  const donde = mapa.sectores.find((s) => s.clave === sectorDeTarifa(mapa, tarifa.id));
  const seleccion = useEditorMapa((s) => s.seleccion);
  const motivo = motivoNoAsignar(mapa, tarifa, seleccion);

  const asignar = () => {
    let resultado = null;
    cambiar((m) => {
      resultado = asignarTarifa(m, seleccion, tarifa.id);
      return resultado.error ? m : resultado.mapa;
    });
    if (resultado?.error) toast.error(resultado.error);
    else if (resultado?.clave) seleccionar({ tipo: "sector", clave: resultado.clave });
  };

  return (
    <Box sx={{ display: "flex", gap: 1.25, px: 2.5, py: 1.5, borderTop: `1px solid ${COLOR.borde}` }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: color, flexShrink: 0, mt: 0.6 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Typography noWrap sx={{ fontSize: 14, fontWeight: 600, color: COLOR.titulo }} title={tarifa.concepto}>
            {tarifa.concepto}
          </Typography>
          <Typography sx={{ fontSize: 13, color: COLOR.texto, flexShrink: 0 }}>{formatoPrecio(tarifa.precio)}</Typography>
        </Box>
        {donde ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <ButtonBase
              onClick={() => seleccionar({ tipo: "sector", clave: donde.clave })}
              sx={{ fontSize: 12, color: COLOR.textoSuave, minWidth: 0, "&:hover": { color: COLOR.acento } }}
            >
              <Typography noWrap component="span" sx={{ fontSize: 12 }}>
                {donde.nombre} · {[donde, ...partesDe(mapa, donde.clave)].reduce((t, s) => t + capacidadSector(s), 0)} lugares
              </Typography>
            </ButtonBase>
            {!fijaPorVentas(tarifa) && (
              <Tooltip title="Quitar de esta ubicación">
                <IconButton size="small" onClick={() => cambiar((m) => quitarTarifa(m, tarifa.id))} sx={{ p: 0.25 }} aria-label="Quitar de esta ubicación">
                  <Close sx={{ fontSize: 14, color: COLOR.textoSuave }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12, color: "#b45309" }}>Sin ubicación: asignala antes de publicar</Typography>
        )}
        {(tarifa.venta_mesa === "mesa" || tarifa.con_ventas) && (
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.75 }}>
            {tarifa.venta_mesa === "mesa" && <Chip size="small" label="Mesa entera" />}
            {tarifa.con_ventas && <Chip size="small" color="warning" variant="outlined" label="Con ventas" />}
          </Box>
        )}
        {tarifa.nueva && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.75 }}>
            <Tooltip title="Se crea al publicar el mapa. Si descartás el borrador, se pierde.">
              <Chip size="small" color="info" variant="outlined" label="Nueva · sin publicar" />
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEditar(tarifa)} aria-label="Editar entrada nueva">
                <EditOutlined sx={{ fontSize: 16, color: COLOR.textoSuave }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Borrar">
              <IconButton size="small" onClick={() => cambiar((m) => quitarTarifaNueva(m, tarifa.id))} aria-label="Borrar entrada nueva">
                <DeleteOutline sx={{ fontSize: 16, color: COLOR.textoSuave }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <BotonConfigurarMesa tarifa={tarifa} sector={donde} />
      </Box>
      <Tooltip title={motivo || ""}>
        <span>
          <Button
            size="small"
            variant="outlined"
            disabled={Boolean(motivo)}
            onClick={asignar}
            sx={{ textTransform: "none", borderRadius: "8px", flexShrink: 0, color: COLOR.acento, borderColor: COLOR.acento }}
          >
            Asignar
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

const PanelTarifas = () => {
  const tarifas = useTarifas();
  const seleccion = useEditorMapa((s) => s.seleccion);
  const mapa = useMapaActual();
  const sinSector = tarifas.filter((t) => !sectorDeTarifa(mapa, t.id)).length;
  const colores = coloresDeTarifas(tarifas);
  const grupos = gruposDe(seleccion);
  const cuantas = (n, una, varias) => (n === 1 ? `1 ${una}` : `${n} ${varias}`);
  const sectorElegido = seleccion?.tipo === "sector" ? mapa.sectores.find((x) => x.clave === seleccion.clave) : null;
  const elegido = sectorElegido
    ? `Elegido: ${sectorElegido.nombre}`
    : grupos.length > 0
      ? `Elegido: ${resumen(elementosDe(seleccion))}`
      : "Elegí en el plano una ubicación, filas o mesas (arrastrando sobre el fondo o con Shift sumás más) y tocá Asignar en una entrada.";
  const hayAlgo = Boolean(sectorElegido) || grupos.length > 0;
  // dialogo: null cerrado, {} para crear, { tarifa } para editar una nueva
  const [dialogo, setDialogo] = useState(null);

  return (
    <Box
      sx={{
        width: ANCHO_PANEL_TARIFAS,
        flexShrink: 0,
        borderRight: `1px solid ${COLOR.borde}`,
        bgcolor: COLOR.panel,
        overflowY: "auto",
      }}
    >
      <SelectorModo />
      <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.6, color: COLOR.textoSuave, textTransform: "uppercase" }}>
            Entradas del evento
          </Typography>
          <Button
            size="small"
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={() => setDialogo({})}
            sx={{ textTransform: "none", color: COLOR.acento, fontWeight: 600, py: 0 }}
          >
            Nueva
          </Button>
        </Box>
        <Typography sx={{ fontSize: 12, color: hayAlgo ? COLOR.titulo : COLOR.textoSuave, mt: 0.5, fontWeight: hayAlgo ? 600 : 400 }}>
          {elegido}
        </Typography>
        {sinSector > 0 && (
          <Typography sx={{ fontSize: 12, color: "#b45309", mt: 1 }}>
            {sinSector === 1 ? "Hay 1 entrada sin ubicación." : `Hay ${sinSector} entradas sin ubicación.`}
          </Typography>
        )}
      </Box>

      {tarifas.length === 0 ? (
        <Typography sx={{ px: 2.5, py: 2, fontSize: 13, color: COLOR.textoSuave, borderTop: `1px solid ${COLOR.borde}` }}>
          El evento todavía no tiene entradas: creá una con "Nueva".
        </Typography>
      ) : (
        tarifas.map((t) => <ItemTarifa key={t.id} tarifa={t} color={colores[t.id]} onEditar={(tarifa) => setDialogo({ tarifa })} />)
      )}
      {dialogo && <DialogoTarifaNueva abierto tarifa={dialogo.tarifa} onCerrar={() => setDialogo(null)} />}
    </Box>
  );
};

export default PanelTarifas;
